import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

/** OnsaFX News - branded placeholder (SVG data URI) */
const makeBrandPlaceholder = (w = 300, h = 200) => {
  const r = Math.round(h * 0.06);      // corner radius
  const iconPad  = h * 0.03;           // padding around the icon
  const iconSize = h * 0.26;           // icon size

  return (
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#1E3A8A"/>
      <stop offset="1" stop-color="#0EA5E9"/>
    </linearGradient>
  </defs>

  <!-- background -->
  <rect width="100%" height="100%" rx="${r}" fill="url(#g)"/>

  <!-- emblem: site icon instead of a circle -->
  <g transform="translate(${w * 0.06}, ${h * 0.18})">
    <rect x="0" y="0" width="${iconSize + iconPad * 2}" height="${iconSize + iconPad * 2}"
          rx="${h * 0.06}" fill="rgba(255,255,255,0.12)"/>
    <image href="/onsa.ico" xlink:href="/onsa.ico"
           x="${iconPad}" y="${iconPad}"
           width="${iconSize}" height="${iconSize}"
           preserveAspectRatio="xMidYMid meet"/>
  </g>

  <!-- brand text -->
  <g transform="translate(${w * 0.36}, ${h * 0.40})">
    <text x="0" y="0" fill="#ffffff" font-family="Inter, Segoe UI, Roboto, Arial"
          font-size="${h * 0.20}" font-weight="700" dominant-baseline="middle">OnsaFX</text>
    <text x="0" y="${h * 0.18}" fill="#ffffff" fill-opacity="0.9" font-family="Inter, Segoe UI, Roboto, Arial"
          font-size="${h * 0.13}" font-weight="600">News</text>
  </g>

  <!-- subtle bottom stripes -->
  <g opacity="0.25" fill="#ffffff" transform="translate(${w * 0.06}, ${h * 0.74})">
    <rect x="0" y="0" width="${w * 0.7}" height="${h * 0.04}" rx="${h * 0.02}"/>
    <rect x="0" y="${h * 0.065}" width="${w * 0.6}" height="${h * 0.03}" rx="${h * 0.015}"/>
  </g>
</svg>`)
  );
};

const PLACEHOLDER = makeBrandPlaceholder();

const NewsCard = ({ title, summary, image, publishedAt, url }) => {
  const [src, setSrc] = useState(image || PLACEHOLDER);
  const [tryNoRef, setTryNoRef] = useState(false);

  useEffect(() => {
    setSrc(image || PLACEHOLDER);
    setTryNoRef(false);
  }, [image]);

  const handleError = () => {
    // Try original URL, then no-referrer, then fallback to branded placeholder
    if (!tryNoRef && image) {
      setTryNoRef(true);
      const bust = image.includes('?') ? '&nr=1' : '?nr=1';
      setSrc(image + bust);
      return;
    }
    if (src !== PLACEHOLDER) setSrc(PLACEHOLDER);
  };

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', height: 100 }}>
      <Box
        component="img"
        src={src}
        alt=""
        title={title}
        loading="lazy"
        referrerPolicy={tryNoRef ? 'no-referrer' : undefined}
        onError={handleError}
        sx={{
          width: { xs: '30%', sm: 134 },
          height: { xs: 72, sm: 96 },
          borderRadius: 1.2,
          objectFit: 'cover',
          display: 'block',
          backgroundColor: '#f0f2f5',
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '10px', sm: '12px' } }}>
          {dayjs(publishedAt).fromNow()}
        </Typography>

        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mt: 0.5, fontSize: { xs: '12px', sm: '14px' }, lineHeight: { xs: '16px', sm: '22px' } }}
        >
          <Link
            href={url}
            underline="always"
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: { xs: '16px', sm: '20px' },
            }}
          >
            {title}
          </Link>
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: { xs: '16px', sm: '20px' },
          }}
        >
          {summary}
        </Typography>
      </Box>
    </Stack>
  );
};

export default NewsCard;
